import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { SnackbarService } from '../services/snackbar.service';

const RELOAD_LOOP_GUARD_KEY = 'chunk-error-reload';
const RELOAD_LOOP_WINDOW_MS = 30 * 1000;
const CHUNK_FAILED_PATTERN =
  /(ChunkLoadError|Loading chunk [\w-]+ failed|Loading CSS chunk|Failed to fetch dynamically imported module)/i;

@Injectable({ providedIn: 'root' })
export class ChunkLoadErrorHandler implements ErrorHandler {
  constructor(private injector: Injector) {}

  handleError(error: any): void {
    const handled = this.tryRecoverFromChunkLoadFailure(error);
    if (!handled) {
      console.error(error);
    }
  }

  tryRecoverFromChunkLoadFailure(error: any): boolean {
    if (!this.isChunkLoadError(error)) return false;
    this.recoverFromChunkLoadFailure();
    return true;
  }

  private recoverFromChunkLoadFailure(): void {
    if (this.isInReloadLoop()) {
      this.notifyReloadLoop();
      return;
    }

    const snackbar = this.tryGetSnackbarService();
    if (!snackbar) {
      this.markReloadAndReload();
      return;
    }

    const ref = snackbar.info('Une nouvelle version de Predik est disponible.', 'Recharger');
    ref.onAction().subscribe(() => this.markReloadAndReload());
  }

  private isChunkLoadError(error: any): boolean {
    if (!error) return false;
    const message = typeof error.message === 'string' ? error.message : '';
    const name = typeof error.name === 'string' ? error.name : '';
    return name === 'ChunkLoadError' || CHUNK_FAILED_PATTERN.test(message) || CHUNK_FAILED_PATTERN.test(name);
  }

  private isInReloadLoop(): boolean {
    try {
      const raw = sessionStorage.getItem(RELOAD_LOOP_GUARD_KEY);
      if (!raw) return false;
      const ts = parseInt(raw, 10);
      return !isNaN(ts) && Date.now() - ts < RELOAD_LOOP_WINDOW_MS;
    } catch {
      return false;
    }
  }

  private notifyReloadLoop(): void {
    const snackbar = this.tryGetSnackbarService();
    const message =
      'Impossible de charger la nouvelle version. Vide le cache du navigateur (Cmd+Shift+R) ou réessaie plus tard.';
    if (snackbar) {
      snackbar.danger(message);
    } else {
      console.error(message);
    }
  }

  private tryGetSnackbarService(): SnackbarService | null {
    try {
      return this.injector.get(SnackbarService);
    } catch {
      return null;
    }
  }

  private markReloadAndReload(): void {
    try {
      sessionStorage.setItem(RELOAD_LOOP_GUARD_KEY, Date.now().toString());
    } catch {
      // sessionStorage indisponible (mode privé strict) : on continue, le worst case est une boucle de reload
    }
    window.location.href = window.location.pathname + '?_v=' + Date.now();
  }
}
